import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';
import DetailSection from '../shared/DetailSection';
import Badge from '../shared/Badge';
import EmptyState from '../shared/EmptyState';
import { getActionLabel, getActionBadgeVariant, getResourceTypeLabel, formatTimestamp } from '../../utils/activityHelpers';

// Activity icon SVG path
const ACTIVITY_ICON = 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01';

export default function UserActivityTab({ userId }) {
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedActivity, setExpandedActivity] = useState(null);

  useEffect(() => {
    if (userId) {
      loadActivities();
    }
  }, [userId]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await adminApi.users.getActivities(userId, 100, 0);
      setActivities(data.activities || []);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error('Failed to load activity data:', error);
      toast.error('Failed to load activity history');
    } finally {
      setLoading(false);
    }
  };

  const toggleActivityDetails = (activityId) => {
    setExpandedActivity(expandedActivity === activityId ? null : activityId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3" style={{ color: 'var(--color-text-muted)' }}>
          <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-gradient-via)', borderTopColor: 'transparent' }}></div>
          <span>Loading activity history...</span>
        </div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="p-8 max-w-5xl">
        <EmptyState
          icon={ACTIVITY_ICON}
          title="No Activity Yet"
          subtitle="This user hasn't performed any tracked actions"
        />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      <div className="space-y-6">
        <DetailSection
          title="Activity History"
          subtitle={`${totalCount} total ${totalCount === 1 ? 'activity' : 'activities'}`}
        >
          <div className="space-y-3">
            {activities.map((activity) => {
              const hasMetadata = activity.activity_metadata && Object.keys(activity.activity_metadata).length > 0;
              return (
              <div
                key={activity.id}
                className="border rounded-lg overflow-hidden transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  borderColor: 'var(--color-border-default)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-gradient-via)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border-default)'}
              >
                {/* Main Row */}
                <div
                  className={`flex items-center gap-4 p-4 ${hasMetadata ? 'cursor-pointer' : ''}`}
                  onClick={() => hasMetadata && toggleActivityDetails(activity.id)}
                >
                  {/* Expand Arrow - Left side */}
                  <div className="flex-shrink-0">
                    {hasMetadata ? (
                      <svg
                        className={`w-5 h-5 transition-transform ${
                          expandedActivity === activity.id ? 'rotate-90' : ''
                        }`}
                        style={{ color: 'var(--color-text-muted)' }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    ) : (
                      <div className="w-5 h-5"></div>
                    )}
                  </div>

                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                      <svg className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ACTIVITY_ICON} />
                      </svg>
                    </div>
                  </div>

                  {/* Activity Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="default" className="text-xs font-medium">
                        {getActionLabel(activity.action)}
                      </Badge>
                      {activity.resource_type && (
                        <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {getResourceTypeLabel(activity.resource_type)}
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      {formatTimestamp(activity.occurred_at)}
                      {activity.ip_address && (
                        <span className="ml-2">• {activity.ip_address}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedActivity === activity.id && activity.activity_metadata && (
                  <div className="border-t p-4" style={{ borderColor: 'var(--color-border-default)', backgroundColor: 'var(--color-bg-secondary)' }}>
                    <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      <div className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Details:</div>
                      <div className="space-y-1">
                        {Object.entries(activity.activity_metadata).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="min-w-[140px]" style={{ color: 'var(--color-text-muted)' }}>{key.replace(/_/g, ' ')}:</span>
                            <span className="break-all" style={{ color: 'var(--color-text-secondary)' }}>
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </DetailSection>
      </div>
    </div>
  );
}
